import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import { toFetchAwareError } from '../utils/fetchErrorMessage';
import { 
  checkSuspiciousActivity, 
  recordLoginAttempt, 
  clearLoginAttempts,
  validateTokenFormat,
  validateUserData,
  checkLocalStorageSecurity
} from '../utils/security';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// تابع برای بررسی اعتبار توکن
const validateToken = async (token) => {
  try {
    const response = await fetch(apiUrl('/api/auth/me'), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    console.log('validateToken - Server response:', data);
    console.log('validateToken - Profile image from server:', data.data?.profileImage ? 'Yes' : 'No');
    return data.success ? data.data : false;
  } catch (error) {
    console.error('خطا در اعتبارسنجی توکن:', error);
    return false;
  }
};

// تابع برای پاک کردن اطلاعات کاربر
const clearUserData = (clearSession = true) => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('profileImage');
  // پاک کردن sessionId و generatedOTP فقط اگر clearSession = true باشد
  if (clearSession) {
    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('generatedOTP');
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [accountDeactivated, setAccountDeactivated] = useState(false);

  // بررسی امنیت کاربر در هر بار بارگذاری
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // بررسی امنیت localStorage
        const securityCheck = checkLocalStorageSecurity();
        if (!securityCheck.secure) {
          console.warn('مشکل امنیتی:', securityCheck.message);
          clearUserData(false); // پاک نکردن sessionId
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          // اگر sessionId وجود دارد، احتمالاً کاربر در حال ثبت نام است
          const sessionId = sessionStorage.getItem('sessionId');
          if (!sessionId) {
            clearUserData(false); // پاک نکردن sessionId
          }
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // بررسی فرمت توکن
        if (!validateTokenFormat(token)) {
          console.warn('فرمت توکن نامعتبر است');
          clearUserData(false); // پاک نکردن sessionId
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // اعتبارسنجی توکن با سرور
        let validUser;
        
        // اگر کاربر تست است، از localStorage استفاده کن
        const storedUser = JSON.parse(userData);
        if (storedUser.isTestUser) {
          console.log('Test user detected, bypassing server validation...');
          validUser = storedUser;
        } else {
          validUser = await validateToken(token);
          
          if (!validUser) {
            clearUserData(false); // پاک نکردن sessionId
            setUser(null);
            setIsAuthenticated(false);
            setLoading(false);
            return;
          }
        }

        // Check if profile image exists in sessionStorage
        const profileImage = sessionStorage.getItem('profileImage');
        console.log('checkAuthStatus - Profile image from sessionStorage:', profileImage ? 'Found' : 'Not found');
        if (profileImage) {
          validUser.profileImage = profileImage;
          console.log('checkAuthStatus - Profile image added to validUser');
        } else {
          // If no profile image in sessionStorage, check if it exists in validUser from server
          console.log('checkAuthStatus - Profile image from server:', validUser.profileImage ? 'Found' : 'Not found');
          if (validUser.profileImage) {
            sessionStorage.setItem('profileImage', validUser.profileImage);
            console.log('checkAuthStatus - Profile image stored in sessionStorage');
          }
        }

        // بررسی صحت اطلاعات کاربر
        if (!validateUserData(validUser)) {
          console.warn('اطلاعات کاربر نامعتبر است');
          clearUserData(false); // پاک نکردن sessionId
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // بررسی تطابق اطلاعات کاربر
        if (validUser.id !== storedUser.id) {
          console.warn('عدم تطابق اطلاعات کاربر');
          clearUserData(false); // پاک نکردن sessionId
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // If account is deactivated, show modal and keep user authenticated for showing dashboard shell
        if (validUser.isActive === false) {
          setAccountDeactivated(true);
        }
        setUser(validUser);
        setIsAuthenticated(true);
        setLoading(false);
      } catch (error) {
        console.error('خطا در بررسی وضعیت احراز هویت:', error);
        clearUserData(false); // پاک نکردن sessionId
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
      }
    };

    checkAuthStatus();

    // تایمر اصلی برای بررسی امنیت کلی (هر 10 دقیقه)
    const mainInterval = setInterval(async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        const validUser = await validateToken(token);
        if (!validUser) {
          clearUserData();
          setUser(null);
          setIsAuthenticated(false);
          setSessionExpired(true);
          // نمایش پیام به کاربر قبل از redirect
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000); // 3 ثانیه تاخیر برای نمایش notification
        }
      }
    }, 10 * 60 * 1000); // 10 دقیقه

    return () => clearInterval(mainInterval);
  }, []); // فقط یک بار اجرا شود



  const login = async (credentials) => {
    try {
      // بررسی فعالیت مشکوک
      const suspiciousCheck = checkSuspiciousActivity();
      if (suspiciousCheck.isSuspicious) {
        throw new Error(suspiciousCheck.message);
      }

      // ثبت تلاش ورود
      recordLoginAttempt(false);

      // پاک کردن اطلاعات قبلی
      clearUserData();
      
      const response = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      // بررسی response status و success flag
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'خطا در ورود به سیستم');
      }

      // بررسی صحت اطلاعات دریافتی
      if (data.requiresOTP) {
        // برای کاربران عادی که نیاز به OTP دارند
        if (!data.sessionId) {
          throw new Error('خطا در ایجاد session');
        }
        
        // ذخیره sessionId برای استفاده در OTP
        sessionStorage.setItem('sessionId', data.sessionId);
        
        return { 
          success: true, 
          requiresOTP: true,
          sessionId: data.sessionId,
          message: data.message 
        };
      } else {
        // برای business و admin که مستقیماً وارد می‌شوند
        if (!data.token || !data.data || !data.data.id) {
          throw new Error('اطلاعات دریافتی نامعتبر است');
        }

        // بررسی فرمت توکن
        if (!validateTokenFormat(data.token)) {
          throw new Error('توکن دریافتی نامعتبر است');
        }

        // بررسی صحت اطلاعات کاربر
        if (!validateUserData(data.data)) {
          throw new Error('اطلاعات کاربر نامعتبر است');
        }

        // ثبت ورود موفق
        recordLoginAttempt(true);
        clearLoginAttempts(); // پاک کردن سابقه تلاش‌ها

        // ذخیره امن اطلاعات
        localStorage.setItem('token', data.token);
        
        // اگر عکس پروفایل وجود دارد، آن را در sessionStorage ذخیره کن
        if (data.data.profileImage) {
          const userDataForStorage = { ...data.data };
          delete userDataForStorage.profileImage;
          localStorage.setItem('user', JSON.stringify(userDataForStorage));
          sessionStorage.setItem('profileImage', data.data.profileImage);
        } else {
          // اطمینان از ذخیره تمام فیلدها شامل isTestUser
          const userDataForStorage = { ...data.data };
          localStorage.setItem('user', JSON.stringify(userDataForStorage));
        }
        
        if (data.data.isActive === false) {
          setAccountDeactivated(true);
        }
        setUser(data.data);
        setIsAuthenticated(true);

        return { success: true, data: data.data };
      }
    } catch (error) {
      throw toFetchAwareError(error, 'auth.loginError');
    }
  };

  // تابع جدید برای تکمیل ورود بعد از تایید OTP
  const completeLoginAfterOTP = async (userData) => {
    try {
      // بررسی صحت اطلاعات کاربر
      if (!validateUserData(userData)) {
        throw new Error('اطلاعات کاربر نامعتبر است');
      }

      // ثبت ورود موفق
      recordLoginAttempt(true);
      clearLoginAttempts(); // پاک کردن سابقه تلاش‌ها

      // اگر عکس پروفایل وجود دارد، آن را در sessionStorage ذخیره کن
      if (userData.profileImage) {
        const userDataForStorage = { ...userData };
        delete userDataForStorage.profileImage;
        localStorage.setItem('user', JSON.stringify(userDataForStorage));
        sessionStorage.setItem('profileImage', userData.profileImage);
      } else {
        localStorage.setItem('user', JSON.stringify(userData));
      }
      
      setUser(userData);
      setIsAuthenticated(true);

      return { success: true, data: userData };
    } catch (error) {
      throw new Error(error.message || 'خطا در تکمیل ورود');
    }
  };

  const register = async (userData) => {
    try {
      // پاک کردن اطلاعات قبلی (به جز sessionId)
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('profileImage');
      
      const response = await fetch(apiUrl('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      // بررسی response status و success flag
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'خطا در ثبت‌نام');
      }

      // در روند جدید، register فقط اطلاعات را در session ذخیره می‌کند
      // و sessionId برمی‌گرداند. کاربر هنوز کامل ثبت نشده است.
      if (!data.sessionId) {
        throw new Error('خطا در ایجاد session');
      }

      // ذخیره sessionId برای استفاده در complete-registration
      sessionStorage.setItem('sessionId', data.sessionId);

      return { 
        success: true, 
        data: data.data, 
        sessionId: data.sessionId,
        otpCode: data.otpCode // اضافه کردن otpCode
      };
    } catch (error) {
      throw toFetchAwareError(error, 'auth.registerError');
    }
  };

  const completeRegistration = async () => {
    try {
      const sessionId = sessionStorage.getItem('sessionId');
      
      if (!sessionId) {
        throw new Error('Session ID یافت نشد. لطفاً دوباره ثبت‌نام کنید.');
      }

      const response = await fetch(apiUrl('/api/auth/complete-registration'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'خطا در تکمیل ثبت‌نام');
      }

      // بررسی صحت اطلاعات دریافتی
      if (!data.token || !data.data || !data.data.id) {
        throw new Error('اطلاعات دریافتی نامعتبر است');
      }

      // ذخیره امن اطلاعات
      localStorage.setItem('token', data.token);
      
      // اگر عکس پروفایل وجود دارد، آن را در sessionStorage ذخیره کن
      if (data.data.profileImage) {
        const userDataForStorage = { ...data.data };
        delete userDataForStorage.profileImage;
        localStorage.setItem('user', JSON.stringify(userDataForStorage));
        sessionStorage.setItem('profileImage', data.data.profileImage);
      } else {
        localStorage.setItem('user', JSON.stringify(data.data));
      }
      
      setUser(data.data);
      setIsAuthenticated(true);

      // پاک کردن sessionId
      sessionStorage.removeItem('sessionId');

      return { success: true, data: data.data };
    } catch (error) {
      throw toFetchAwareError(error, 'auth.registerError');
    }
  };

  const logout = () => {
    clearUserData(true); // پاک کردن sessionId در logout
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token || !isAuthenticated) {
        throw new Error('شما مجاز به انجام این عملیات نیستید');
      }

      const response = await fetch(apiUrl('/api/auth/update-profile'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        // اگر توکن منقضی شده، کاربر را logout کن
        if (response.status === 401) {
          logout();
          throw new Error('جلسه شما منقضی شده است. لطفاً دوباره وارد شوید.');
        }
        throw new Error(data.message || 'خطا در بروزرسانی پروفایل');
      }

      // بررسی صحت اطلاعات دریافتی
      if (!data.data || !data.data.id) {
        throw new Error('اطلاعات دریافتی نامعتبر است');
      }

      // Update local storage and state
      const updatedUser = { ...user, ...data.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      return { success: true, data: data.data };
    } catch (error) {
      throw toFetchAwareError(error, 'auth.profileUpdateError');
    }
  };

  const updateUserProfile = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token || !isAuthenticated) {
        throw new Error('شما مجاز به انجام این عملیات نیستید');
      }

      // Update local storage and state immediately
      const updatedUser = { ...user, ...userData };
      
      // For profile image, store in sessionStorage to avoid localStorage size issues
      if (userData.profileImage) {
        // Store user data without profile image in localStorage
        const userDataForStorage = { ...updatedUser };
        delete userDataForStorage.profileImage;
        localStorage.setItem('user', JSON.stringify(userDataForStorage));
        // Store profile image in sessionStorage
        sessionStorage.setItem('profileImage', userData.profileImage);
        // Keep profile image in user object for immediate display
        updatedUser.profileImage = userData.profileImage;
      } else {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      
      setUser(updatedUser);

      return { success: true, data: userData };
    } catch (error) {
      console.error('خطا در بروزرسانی پروفایل کاربر:', error);
      throw new Error(error.message || 'خطا در بروزرسانی پروفایل');
    }
  };

  // تابع جدید برای به‌روزرسانی عکس پروفایل
  const updateProfileImage = async (profileImage) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token || !isAuthenticated) {
        throw new Error('شما مجاز به انجام این عملیات نیستید');
      }

      console.log('updateProfileImage called with:', profileImage);
      console.log('Current user before update:', user);

      // Update user state immediately with new profile image
      const updatedUser = { ...user, profileImage };
      console.log('Updated user:', updatedUser);
      
      setUser(updatedUser);
      
      // Store profile image in sessionStorage
      sessionStorage.setItem('profileImage', profileImage);
      console.log('Profile image stored in sessionStorage');
      
      // Store user data without profile image in localStorage
      const userDataForStorage = { ...updatedUser };
      delete userDataForStorage.profileImage;
      localStorage.setItem('user', JSON.stringify(userDataForStorage));
      console.log('User data stored in localStorage without profile image');

      return { success: true, data: { profileImage } };
    } catch (error) {
      console.error('خطا در بروزرسانی عکس پروفایل:', error);
      throw new Error(error.message || 'خطا در بروزرسانی عکس پروفایل');
    }
  };

  const value = {
    user,
    login,
    completeLoginAfterOTP,
    register,
    completeRegistration,
    logout,
    updateUser,
    updateUserProfile,
    updateProfileImage,
    loading,
    isAuthenticated,
    sessionExpired,
    setSessionExpired,
    accountDeactivated,
    setAccountDeactivated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 